import { isSameDay, parseISO } from 'date-fns';

import { TimeOffRequest, TimeOffRequestDay } from '@/modules/time-off/types/time-off-request';

import { TimeOffRequestSummaryStatus } from '../types/time-tracking';

/**
 * The minimal `timeOffSummary`/`timeOff` items carry only status + hours, while
 * the full detail lives in the separate `timeOffRequests` array. These helpers
 * correlate the two by date + status so collapsed week cells and tooltips can
 * show the request's type, part-of-day, description and approvers.
 *
 * Summary status (`PENDING | APPROVED`) and request status share the same wire
 * values, so a direct string comparison is sufficient.
 */

export interface MatchedTimeOffDay {
  request: TimeOffRequest;
  day: TimeOffRequestDay;
}

/** Find the request (and its matching day) covering `date` with the given summary status. */
export const findTimeOffRequestForDay = (
  requests: TimeOffRequest[],
  date: Date,
  status: TimeOffRequestSummaryStatus
): MatchedTimeOffDay | undefined => {
  for (const request of requests) {
    if (request.status !== (status as unknown as TimeOffRequest['status'])) continue;
    const day = request.days.find(d => isSameDay(parseISO(d.date), date));
    if (day) return { request, day };
  }
  return undefined;
};

/** All requests whose status matches and that have at least one day on `date`. */
export const findTimeOffRequestsForDate = (
  requests: TimeOffRequest[],
  date: Date
): MatchedTimeOffDay[] =>
  requests.flatMap(request => {
    const day = request.days.find(d => isSameDay(parseISO(d.date), date));
    return day ? [{ request, day }] : [];
  });
